// /login/set-password — first-login force-password-change screen.
//
// Cognito returns NEW_PASSWORD_REQUIRED on the very first sign-in of any
// admin-invited user. The state machine lands here.
//
// Visual + copy built from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 6b.
// On submit, calls useAuth().submitNewPassword(); on success the state
// machine continues into MFA setup (pool requires MFA, user hasn't enrolled
// yet on first login).

import { useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Card,
  DEFAULT_PASSWORD_RULES,
  LangToggle,
  PasswordRequirements,
  TextInput,
} from '@gc-coach/design-system';
import { AdminAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

export function FirstLoginPasswordScreen() {
  const { t, tt, lang, setLang } = useI18n();
  const { status, error, submitNewPassword, clearError } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touchedConfirm, setTouchedConfirm] = useState(false);

  const submitting = status.kind === 'submitting-new-password';
  const email = status.kind === 'new-password-required' ? status.email : '';
  const allRulesPass = DEFAULT_PASSWORD_RULES.every((r) => r.test(password));
  const confirmMismatch = touchedConfirm && confirm !== '' && password !== confirm;
  const canSubmit =
    !submitting && password.length > 0 && password === confirm && allRulesPass;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    void submitNewPassword(password);
  };

  // Body has {email} bolded inline.
  const bodyParts = tt('firstLogin.body', { email: ' EMAIL ' }).split(' EMAIL ');

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
          <LangToggle value={lang} onChange={setLang} />
        </div>

        <div className={styles.formColumn}>
          <header className={styles.header}>
            <span className={styles.eyebrow}>{t('firstLogin.eyebrow')}</span>
            <h1 className={styles.title}>{t('firstLogin.headline')}</h1>
            <p className={styles.body}>
              {bodyParts[0]}
              <strong className={styles.bodyEmphasis}>{email}</strong>
              {bodyParts[1]}
            </p>
          </header>

          <Card>
            <form onSubmit={onSubmit} className={styles.form} noValidate>
              {error && (
                <Alert severity="danger" onDismiss={clearError}>
                  {error.message}
                </Alert>
              )}

              <TextInput
                label={t('firstLogin.newPasswordLabel')}
                type="password"
                name="new-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                disabled={submitting}
                revealable
                required
                autoFocus
              />

              <TextInput
                label={t('firstLogin.confirmPasswordLabel')}
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.currentTarget.value)}
                onBlur={() => setTouchedConfirm(true)}
                disabled={submitting}
                revealable
                required
                errorMessage={confirmMismatch ? t('firstLogin.errorMismatch') : undefined}
              />

              <PasswordRequirements value={password} />

              <Button
                type="submit"
                kind="brand"
                fullWidth
                loading={submitting}
                disabled={!canSubmit}
              >
                {t('firstLogin.submitButton')}
              </Button>
            </form>
          </Card>

          <div className={styles.calloutInfo}>{t('firstLogin.callout')}</div>
        </div>
    </AdminAuthShell>
  );
}
